/**
 * Clip-cache status + the serial caching queue.
 *
 * "Cached" is derived from the ClipCache index (which is itself derived from
 * actual Cache API contents), never from bookkeeping that could outlive a
 * browser eviction. Queue states are layered on top.
 *
 * The queue is SERIAL by design: a remux saturates network and CPU, so running
 * several at once makes each slower and multiplies long-lived connections on
 * the single-process Django proxy.
 */
import { create } from 'zustand';
import type { Archive } from '../api/archives';
import { getClipCache, sourceKeyOf } from '../engine/clipcache';
import { mediaParams, sourceMediaUrl } from '../engine/mediaurls';
import { remuxRange, RemuxCancelledError } from '../engine/remux';
import type { AudioClip, Edit, VideoClip } from '../format/types';

export type ClipCacheStatus =
  | { kind: 'none' }
  | { kind: 'queued' }
  | { kind: 'caching'; progress: number }
  | { kind: 'cached' }
  | { kind: 'error'; message: string };

const NONE: ClipCacheStatus = { kind: 'none' };
const CACHED: ClipCacheStatus = { kind: 'cached' };
const QUEUED: ClipCacheStatus = { kind: 'queued' };

interface ClipCacheState {
  byClip: Record<string, ClipCacheStatus>;
  /** Clips still queued or in flight — drives the toolbar readout. */
  pending: number;
  totalQueued: number;
  usageBytes: number;
  setAll(byClip: Record<string, ClipCacheStatus>, pending: number, totalQueued: number): void;
  setUsage(bytes: number): void;
}

export const useClipCacheStore = create<ClipCacheState>((set) => ({
  byClip: {},
  pending: 0,
  totalQueued: 0,
  usageBytes: 0,
  setAll: (byClip, pending, totalQueued) => set({ byClip, pending, totalQueued }),
  setUsage: (usageBytes) => set({ usageBytes }),
}));

function sameStatus(a: ClipCacheStatus | undefined, b: ClipCacheStatus): boolean {
  if (!a || a.kind !== b.kind) return false;
  if (a.kind === 'caching' && b.kind === 'caching') return a.progress === b.progress;
  if (a.kind === 'error' && b.kind === 'error') return a.message === b.message;
  return true;
}

// ---- queue state (module-local; the store mirrors it for React) ------------

interface Job {
  clipId: string;
  sourceKey: string;
  url: string;
  in: number;
  out: number;
}

/**
 * Largest clip we will remux, by estimated output size.
 *
 * The muxer accumulates the whole output in memory before it can be stored, and
 * peak usage runs 2-3x the output size (buffer + blob + copy). Measured ~15MB
 * per minute at 480p, so this is roughly 40 minutes — beyond that the tab is at
 * real risk of being killed mid-run. Enforced from the metadata pre-pass, so an
 * over-limit clip is refused instantly rather than after minutes of downloading.
 */
const MAX_CLIP_BYTES = 600 * 1024 * 1024;

/**
 * Hard duration cap, checked with no I/O at all.
 *
 * MAX_CLIP_BYTES alone is not enough: it is enforced from the metadata
 * pre-pass, which must walk every packet in the range — for a 100-minute clip
 * that is ~160k packets and takes minutes, so the user would wait a long time
 * just to be refused. This gate answers instantly.
 */
const MAX_CLIP_SECONDS = 40 * 60;

let queue: Job[] = [];
let active: { clipId: string; progress: number } | null = null;
let cancelToken = { cancelled: false };
let draining = false;
const errors = new Map<string, string>();
let totalQueued = 0;

function mediaClips(edit: Edit): (VideoClip | AudioClip)[] {
  const out: (VideoClip | AudioClip)[] = [];
  for (const track of edit.tracks) {
    if (track.kind === 'overlay') continue;
    out.push(...(track.clips as (VideoClip | AudioClip)[]));
  }
  return out;
}

/** Cache identity for a clip, matching exactly what the preview streams. */
function clipTarget(
  edit: Edit,
  archives: Archive[],
  clip: VideoClip | AudioClip,
): { sourceKey: string; url: string } | null {
  const src = edit.sources[clip.source];
  if (!src || src.kind !== 'pandora-video') return null;
  const archive = archives.find((a) => a.key === src.archive);
  if (!archive) return null;
  const p = mediaParams(archive);
  const url = sourceMediaUrl(src, archives);
  if (!url) return null;
  return {
    sourceKey: sourceKeyOf(src.archive, src.item, src.part, p.resolution, p.format),
    url: new URL(url, window.location.href).toString(),
  };
}

/** Recompute every clip's status from the cache index + queue state. */
export function syncClipCacheStatus(edit: Edit, archives: Archive[]): void {
  const cache = getClipCache();
  const prev = useClipCacheStore.getState().byClip;
  const next: Record<string, ClipCacheStatus> = {};
  const queuedIds = new Set(queue.map((j) => j.clipId));

  for (const clip of mediaClips(edit)) {
    let status: ClipCacheStatus = NONE;
    const target = clipTarget(edit, archives, clip);
    if (target && cache.lookup(target.sourceKey, clip.in, clip.out)) status = CACHED;
    else if (active?.clipId === clip.id) status = { kind: 'caching', progress: active.progress };
    else if (queuedIds.has(clip.id)) status = QUEUED;
    else {
      const err = errors.get(clip.id);
      if (err) status = { kind: 'error', message: err };
    }
    // Reuse the previous object when unchanged so ClipBlock selectors don't
    // fire — otherwise one caching clip re-renders every clip on the timeline.
    next[clip.id] = sameStatus(prev[clip.id], status) ? prev[clip.id] : status;
  }

  const pending = queue.length + (active ? 1 : 0);
  useClipCacheStore.getState().setAll(next, pending, pending ? totalQueued : 0);
  useClipCacheStore.getState().setUsage(cache.usage().bytes);
}

/** Queue clips for caching (Shift+R). Already-cached clips are skipped. */
export function cacheClips(clipIds: string[], edit: Edit, archives: Archive[]): void {
  const cache = getClipCache();
  const known = new Set(queue.map((j) => j.clipId));
  for (const clip of mediaClips(edit)) {
    if (!clipIds.includes(clip.id) || known.has(clip.id) || active?.clipId === clip.id) continue;
    const target = clipTarget(edit, archives, clip);
    if (!target) continue;
    if (cache.lookup(target.sourceKey, clip.in, clip.out)) continue; // already cached
    errors.delete(clip.id);
    const seconds = clip.out - clip.in;
    if (seconds > MAX_CLIP_SECONDS) {
      errors.set(
        clip.id,
        `clip is ${Math.round(seconds / 60)} min — too long to cache in one piece (limit ${MAX_CLIP_SECONDS / 60} min). Split it and cache the pieces.`,
      );
      continue;
    }
    queue.push({ clipId: clip.id, ...target, in: clip.in, out: clip.out });
  }
  queue.sort((a, b) => {
    const ca = mediaClips(edit).find((c) => c.id === a.clipId);
    const cb = mediaClips(edit).find((c) => c.id === b.clipId);
    return (ca?.start ?? 0) - (cb?.start ?? 0);
  });
  totalQueued = queue.length + (active ? 1 : 0);
  syncClipCacheStatus(edit, archives);
  void drain(edit, archives);
}

export function cancelCaching(edit: Edit, archives: Archive[]): void {
  queue = [];
  cancelToken.cancelled = true;
  syncClipCacheStatus(edit, archives);
}

export function isCaching(): boolean {
  return draining;
}

async function drain(edit: Edit, archives: Archive[]): Promise<void> {
  if (draining) return;
  draining = true;
  cancelToken = { cancelled: false };
  const cache = getClipCache();
  await cache.init();
  void cache.requestPersist();

  try {
    while (queue.length) {
      const job = queue.shift()!;
      active = { clipId: job.clipId, progress: 0 };
      syncClipCacheStatus(edit, archives);
      try {
        const res = await remuxRange(job.url, job.in, job.out, {
          cancel: cancelToken,
          maxBytes: MAX_CLIP_BYTES,
          onProgress: (p) => {
            // Quantized: raw progress would push a store update per packet.
            const q = Math.round(p * 50) / 50;
            if (active && active.progress !== q) {
              active.progress = q;
              syncClipCacheStatus(edit, archives);
            }
          },
        });
        await cache.put(
          job.sourceKey,
          res.startOffset,
          res.startOffset + res.duration,
          res.blob,
          res.hitEnd,
        );
        await cache.evictToBudget();
        // Safety net: if the entry we just wrote does not satisfy the coverage
        // check, the clip would silently revert to "not cached" and re-download
        // on every attempt. Surface it instead of losing the work quietly.
        if (!cache.lookup(job.sourceKey, job.in, job.out)) {
          errors.set(
            job.clipId,
            `cached ${res.startOffset.toFixed(1)}–${(res.startOffset + res.duration).toFixed(1)}s but the clip needs ${job.in.toFixed(1)}–${job.out.toFixed(1)}s`,
          );
        }
      } catch (e) {
        if (e instanceof RemuxCancelledError || cancelToken.cancelled) break;
        const msg = e instanceof Error ? e.message : String(e);
        errors.set(job.clipId, msg);
        cache.markFailed(job.sourceKey, job.in, job.out);
      } finally {
        active = null;
      }
      syncClipCacheStatus(edit, archives);
    }
  } finally {
    draining = false;
    active = null;
    totalQueued = 0;
    syncClipCacheStatus(edit, archives);
  }
}

/** Test/teardown hook. */
export function __resetClipCacheQueue(): void {
  queue = [];
  active = null;
  draining = false;
  errors.clear();
  totalQueued = 0;
}
